import useFetchAndTrace, {
  FetchAndTraceErrorHandler,
  FetchAndTraceHandler,
} from "../../../fetchWithTrace";
import settings from "../../../../../constants/settings";
import { useCallback, useContext, useState } from "react";
import { trackBoardEvent } from "../../../../observability/boardTracking";
import { DEMO_BOARD_SLUG } from "../../../../../constants/boards";
import { useCustomAlert } from "../../../alerts";
import { setSendingBoardEvent } from "../../../../../reducers/uploadedBoard";
import { BoardIdContext } from "./reviewBoardFlowLoader";
import { useAppDispatch } from "../../../store";

export enum BoardTransitionEvent {
  BOARD_CREATED_EVENT = "Board created",
  GERBERS_UPLOADED_EVENT = "Gerbers uploaded",
  PHYSICAL_DETAILS_PROCESSED = "Physical details processed",
  PHYSICAL_DETAILS_UPDATED = "Physical details updated",
  BOM_PROCESSED = "Bill of materials processed",
  BOM_UPDATED = "Bill of materials updated",
  SHOULD_CHECK_PLACEMENTS = "Should check placements",
  SHOULD_CHECK_SELECTIONS = "Should check selections",
  SHOULD_UPDATE_PHYSICAL_DETAILS = "Should update physical details",
  SHOULD_UPDATE_FILE_MAPPING = "Should update file mapping",
  SHOULD_DISMISS_DRC = "Should dismiss DRC",
  SHOULD_REPROCESS_PHYSICAL_DETAILS = "Should reprocess physical details",
  SHOULD_UPDATE_BOM = "Should update bom",
  SHOULD_UPDATE_SHIPMENT = "Should update shipment",
  SHOULD_REMOVE_SHIPMENT = "Should remove shipment",
  SHOULD_UPDATE_CUSTOMER_SPECIFICATIONS = "Should update customer specifications",
  QUOTE_RESET = "Quote reset",
  QUOTE_CALCULATED = "Quote calculated",
  REQUEST_CUSTOM_QUOTE = "Request custom quote",
  CANCEL_CUSTOM_QUOTE_REQUEST = "Cancel custom quote request",
  SET_CUSTOM_QUOTE = "Set custom quote",
  QUOTE_CHECKED = "Quote checked",
  SHOULD_CREATE_FAB_DRAWINGS = "Should create fab drawings",
  FAB_DRAWINGS_CREATED = "Fab drawings created",
  BOARD_ADDED_TO_CART_EVENT = "Board added to cart",
  BOARD_REMOVED_FROM_CART_EVENT = "Board removed from cart",
  DELETE_BOARD_EVENT = "Deletion requested",
  BOARD_ORDER_ACCEPTED = "Board order accepted",
  BOARD_ORDER_CANCELED = "Board order canceled",
  BOARD_LOADED_EVENT = "Board loaded",
  SHIP_DATE_UPDATED = "Ship date updated",
  MANUFACTURING_STEP_UPDATE = "Manufacturing step update",
  SHIPMENT_UPDATED = "Shipment updated",
  SHIPMENT_PROCESSED = "Shipment processed",
  SHIPMENT_SHOULD_PURCHASE_LABEL = "Shipment should purchase label",
  SHIPMENT_SHOULD_UPDATE_MANUFACTURED = "Shipment should update manufactured",
  SHOULD_UPDATE_PANELS = "Should update panels",
  SHOULD_UPDATE_STENCILS = "Should update stencils",
  ADD_STENCIL = "Add stencil",
  REMOVE_STENCIL = "Remove stencil",
  REMOVE_PANELS = "Remove panels",
  ADD_PANELS = "Add panels",
  SHOULD_UPDATE_PNP_TRANSFORMATIONS = "Should update pnp transformations",
  SEND_MESSAGE = "Send message",
  DELETE_MESSAGE = "Delete message",
  UPDATE_MESSAGE_STATUS = "Update message status",
  UPDATE_NICKNAME = "Update nickname",
  UPDATE_BOARD_REVISION = "Update board revision",
  SHOULD_PROCESS_BOARD_REVISION = "Should process board revision",
  BOARD_REVISION_PROCESSED = "Board revision processed",
  CUSTOMER_FILES_UPLOADED = "Customer files uploaded",
  REMOVE_CUSTOMER_FILE = "Remove customer file",
}

export enum MachineGroup {
  BOARD = "Board group",
  BOARD_REVISIONS = "Board revision group",
  PART_SELECTION = "Part selection",
  ORDER = "Order group",
  SHIPMENT = "Shipment group",
  INVOICE = "Invoice group",
}

export interface BoardEvent {
  event: BoardTransitionEvent;
  model_id: string;
  machine_group: MachineGroup;
  update?: object | null;
}

export default function useSendBoardEvent(
  onError?: () => void,
  onSuccessfulEvent?: () => void,
) {
  const fetchAndTrace = useFetchAndTrace();
  const [loading, setLoading] = useState(false);
  const customAlert = useCustomAlert();
  const boardIdFromContext = useContext(BoardIdContext)?.boardId || null;
  const dispatch = useAppDispatch();

  const handler: FetchAndTraceHandler = useCallback(
    async (response) => {
      setLoading(false);
      if (response.ok) {
        onSuccessfulEvent?.();
        if (boardIdFromContext !== null) {
          dispatch(
            setSendingBoardEvent({
              boardId: boardIdFromContext,
              sending: false,
            }),
          );
        }
      } else if (response.status === 400) {
        const { detail } = (await response.json()) as { detail: string };
        customAlert(detail);
      } else {
        throw Error("Failed to send board event");
      }
    },
    [boardIdFromContext, onSuccessfulEvent, dispatch, customAlert],
  );

  const errorHandler: FetchAndTraceErrorHandler = useCallback(() => {
    setLoading(false);
    if (boardIdFromContext !== null) {
      dispatch(
        setSendingBoardEvent({ boardId: boardIdFromContext, sending: false }),
      );
    }
    if (onError !== undefined) {
      onError();
    }
  }, [boardIdFromContext, dispatch, onError]);

  const sendBoardEvent = useCallback(
    async (boardId: string, event: BoardEvent) => {
      if (boardId != DEMO_BOARD_SLUG) {
        trackBoardEvent(boardId, event);
        setLoading(true);
        if (boardIdFromContext !== null) {
          dispatch(
            setSendingBoardEvent({
              boardId: boardIdFromContext,
              sending: true,
            }),
          );
        }
        await fetchAndTrace(
          `${settings.api_url}/boards/${boardId}/event`,
          handler,
          errorHandler,
          {
            body: JSON.stringify(event),
            method: "POST",
          },
        );
      }
    },
    [dispatch, errorHandler, fetchAndTrace, handler, boardIdFromContext],
  );

  return {
    sendBoardEvent,
    loading,
  };
}
